import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Input, Space, Tag, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getCareProtocolFindAllQueryKey,
  useCareProtocolFindAll,
  useCareProtocolResetItem,
  useCareProtocolSetItem,
} from '../../../libs/ohat-api';
import {
  CARE_PROTOCOL_SCORES,
  CareProtocolItem,
  careProtocolKey,
} from '../../../types/care-protocol';
import { ALL_QUESTIONS } from '../../../constants/question';
import { OhatStatus } from '../../../constants/status';
import { usePlan } from '../../../hooks/usePlan';
import { LockedFeature } from '../../components/common/LockedFeature';
import { CareProtocolPageBreadCrumb } from '../../components/breadcrumb/CareProtocolPageBreadCrumb';
import { getApiErrorMessage } from '../../../utils/apiError';

export function CareProtocolPage() {
  const { message, modal } = App.useApp();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { canUse } = usePlan();
  const editable = canUse('care_protocol');
  const queryClient = useQueryClient();

  const { data } = useCareProtocolFindAll();
  const items = useMemo(() => (data ?? []) as CareProtocolItem[], [data]);
  const { mutateAsync: updateItem } = useCareProtocolSetItem();
  const { mutateAsync: resetItem } = useCareProtocolResetItem();

  // 項目×スコアごとの編集中テキスト。キーは `item:score`。取得結果で初期化する。
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // 実行中の項目×スコアのキー（保存・リセットで別管理）。
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [resettingKey, setResettingKey] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const it of items)
      next[careProtocolKey(it.item, it.score)] = it.content;
    setDrafts(next);
  }, [items]);

  /** 一覧を再取得して最新の内容・isCustom を反映する。 */
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getCareProtocolFindAllQueryKey(),
    });

  const handleSave = async (item: number, score: number) => {
    const key = careProtocolKey(item, score);
    try {
      setSavingKey(key);
      await updateItem({
        id: String(item),
        data: { score: score as 1 | 2, content: drafts[key] },
      });
      await invalidate();
      message.success(t('careProtocol.saved'));
    } catch (e) {
      console.error('care protocol save error:', e);
      message.error(getApiErrorMessage(e, t('careProtocol.saveFailed')));
    } finally {
      setSavingKey(null);
    }
  };

  /** 組織カスタムを削除して共通デフォルトに戻す（確認ダイアログあり）。 */
  const handleReset = (item: number, score: number) => {
    const key = careProtocolKey(item, score);
    modal.confirm({
      title: t('careProtocol.resetConfirmTitle'),
      content: t('careProtocol.resetConfirmDesc'),
      okText: t('careProtocol.reset'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          setResettingKey(key);
          await resetItem({ id: String(item), score: String(score) });
          await invalidate();
          message.success(t('careProtocol.resetDone'));
        } catch (e) {
          console.error('care protocol reset error:', e);
          message.error(getApiErrorMessage(e, t('careProtocol.resetFailed')));
        } finally {
          setResettingKey(null);
        }
      },
    });
  };

  /** 1件（項目×スコア）の編集ブロック。 */
  const renderItem = (it: CareProtocolItem) => {
    const key = careProtocolKey(it.item, it.score);
    const draft = drafts[key] ?? '';
    const changed = draft !== it.content;
    const status = OhatStatus.getByScore(it.score);
    return (
      <div key={key} style={{ marginTop: 8 }}>
        <Space size={8} style={{ marginBottom: 4 }}>
          <Typography.Text style={{ fontSize: 13 }}>
            {t('careProtocol.scoreLabel', {
              score: it.score,
              status: status?.getLabel(lang) ?? '',
            })}
          </Typography.Text>
          <Tag
            color={it.isCustom ? 'blue' : 'default'}
            style={{ marginInlineEnd: 0 }}
          >
            {it.isCustom ? t('careProtocol.custom') : t('careProtocol.default')}
          </Tag>
        </Space>
        <Input.TextArea
          value={draft}
          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
          maxLength={5000}
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder={t('careProtocol.placeholder')}
          disabled={!editable}
        />
        {editable && (
          <div style={{ textAlign: 'right', marginTop: 6 }}>
            <Space size={8}>
              {it.isCustom && (
                <Button
                  size="small"
                  danger
                  loading={resettingKey === key}
                  onClick={() => handleReset(it.item, it.score)}
                >
                  {t('careProtocol.reset')}
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                disabled={!changed || !draft.trim()}
                loading={savingKey === key}
                onClick={() => handleSave(it.item, it.score)}
              >
                {t('careProtocol.save')}
              </Button>
            </Space>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, maxWidth: 900, width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <CareProtocolPageBreadCrumb />
      </div>

      {/* ケアプロトコルは組織全体で共有される設定のため、常時注意書きを表示する */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        title={t('careProtocol.orgWideNotice')}
      />

      {!editable && (
        <div style={{ marginBottom: 16 }}>
          <LockedFeature featureKey="care_protocol" />
        </div>
      )}

      <Card title={t('careProtocol.title')}>
        <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
          {t('careProtocol.description')}
        </Typography.Paragraph>

        <Space orientation="vertical" size={20} style={{ width: '100%' }}>
          {ALL_QUESTIONS.map((q, index) => {
            const item = index + 1;
            return (
              <div key={item}>
                <Typography.Text strong style={{ display: 'block' }}>
                  {`${q.key}. ${q.getLabel(lang)}`}
                </Typography.Text>
                {CARE_PROTOCOL_SCORES.map((score) => {
                  const it = items.find(
                    (i) => i.item === item && i.score === score,
                  );
                  return it ? renderItem(it) : null;
                })}
              </div>
            );
          })}
        </Space>
      </Card>
    </div>
  );
}
